/**
 * Phase 4b PR 4 — feature flag helpers.
 * Phase 4 PR 10 — soft launch: build-time default flipped from OFF to ON.
 *
 * The chat surface ships at `/v2/chat` and is now ON BY DEFAULT.
 * Pre-PR-10, users had to type `?chat_v2=1` to opt in; post-PR-10,
 * navigating directly to `/v2/chat` works without any URL gymnastics.
 *
 * Opt-out is still supported (PR 10 keeps the disable path symmetrical
 * with the enable path so anyone who breaks can flip back instantly):
 *
 *   - URL param `?chat_v2=0` (or `false` / `no` / `off`) — disables AND
 *     persists the disable preference to localStorage
 *   - localStorage flag `wai_react_chat_v2_enabled=false` — disables
 *
 * Resolution order (first match wins):
 *
 *   1. URL query param: `?chat_v2=1|0|true|false|yes|no|on|off` (case
 *      insensitive). Persists the chosen direction to localStorage so
 *      refreshes / tabs honor the most recent explicit choice.
 *   2. localStorage flag: explicit `'true'` or `'false'` string —
 *      respects the user's most recent explicit opt-out.
 *   3. **Build-time default: ON (PR 10 flip).** Pre-PR-10 this was
 *      OFF; the soft launch flips the default-on so the React chat
 *      surface is now the path of least resistance for `/v2/chat`
 *      visitors.
 *
 * Vanilla `/` is still the landing page — gotcha #6 stays in force
 * until Phase 7 cutover. This flag governs `/v2/chat` ONLY.
 *
 * Pure functions; no React. The hook lives in hooks/useFeatureFlag.ts.
 */

const STORAGE_KEY = 'wai_react_chat_v2_enabled';
const URL_PARAM = 'chat_v2';
// PR 10: build-time default. Was `false` pre-PR-10. Phase 7 cutover will
// retire this constant entirely (the flag becomes always-on as the
// React chat IS the chat surface, not a v2).
const DEFAULT_ENABLED = true;

// CI-1 — coach-insight rules engine (the /v2/today CoachCard +
// CoachStories real-data source). Frontend-only flag, same resolution
// chain as chat_v2. CI-3 flipped the default ON after the CI-2 live
// smoke; opt out with `?coach_insights=0`.
const INSIGHTS_STORAGE_KEY = 'wai_coach_insights_enabled';
const INSIGHTS_URL_PARAM = 'coach_insights';
const INSIGHTS_DEFAULT_ENABLED = true;

// NU-2 — plan-lifecycle in-app nudges (draft next week / log sessions /
// season wrap) on the proactive-notification pipeline. Frontend-only
// flag, same resolution chain. NU-3 flipped the default ON after the
// flag-dark live verify; opt out with `?plan_nudges=0` or the Settings
// "Plan reminders" toggle (the `_notification_prefs.plan_nudge` gate).
const PLAN_NUDGES_STORAGE_KEY = 'wai_plan_nudges_enabled';
const PLAN_NUDGES_URL_PARAM = 'plan_nudges';
const PLAN_NUDGES_DEFAULT_ENABLED = true;

// NP-0 — canonical DB-truth plan table in chat. When a chat turn persists
// a plan (DB-confirmed `plan_persist` frame / POST contract), the message
// renders the CANONICAL table fetched from training_plans + plan_days
// instead of whatever markdown the LLM happened to emit. Frontend-only
// flag, same resolution chain. Ships default ON (single PR — the change
// is additive rendering with the old behavior as its own fallback);
// opt out with `?canonical_plan_table=0`.
const CANONICAL_TABLE_STORAGE_KEY = 'wai_canonical_plan_table_enabled';
const CANONICAL_TABLE_URL_PARAM = 'canonical_plan_table';
const CANONICAL_TABLE_DEFAULT_ENABLED = true;

// B-19 (b19_actuals_v1) — planned-vs-actual distance pairing on the WeekPlan
// grid + PlanTable card (and the reward-strip "done" honesty fix). Frontend-
// only display flag, same resolution chain. Ships default OFF: absent → the
// surfaces render planned-only, byte-identical to pre-B-19. Opt in with
// `?b19_actuals=1`. (The write path — `plan_days.actual_distance` capture — is a
// separate, already-live flag: `actual_distance_capture_v1`.)
const B19_ACTUALS_STORAGE_KEY = 'wai_b19_actuals_enabled';
const B19_ACTUALS_URL_PARAM = 'b19_actuals';
const B19_ACTUALS_DEFAULT_ENABLED = false;

/**
 * CI-1 — the shared resolution chain, extracted verbatim from
 * isReactChatV2Enabled so every frontend flag resolves identically:
 *
 *   1. URL query param `?<urlParam>=1|0|true|false|yes|no|on|off`
 *      (case-insensitive) — wins over localStorage either direction,
 *      and persists the chosen direction so refreshes / tabs honor
 *      the most recent explicit choice.
 *   2. localStorage `<storageKey>` — explicit 'true' or 'false' only.
 *   3. The build-time default.
 *
 * SSR-safe: returns the default if `window` is undefined.
 */
function resolveFlag(urlParam: string, storageKey: string, defaultEnabled: boolean): boolean {
  if (typeof window === 'undefined') return defaultEnabled;

  // 1. Check URL param first — wins over localStorage either direction
  const url = new URL(window.location.href);
  const param = url.searchParams.get(urlParam);
  if (param !== null) {
    const lc = param.toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(lc)) {
      try {
        window.localStorage.setItem(storageKey, 'true');
      } catch {
        // private mode / quota — best effort
      }
      return true;
    }
    if (['0', 'false', 'no', 'off'].includes(lc)) {
      // PR 10: persist the explicit disable so subsequent loads
      // without the URL param still respect the opt-out. Removing
      // the key instead would silently re-enable a default-ON flag
      // on the next load — exactly the wrong behavior.
      try {
        window.localStorage.setItem(storageKey, 'false');
      } catch {
        // best effort
      }
      return false;
    }
    // unrecognized values: ignore and fall through to localStorage
  }

  // 2. Check localStorage — explicit 'true' or 'false' wins over default
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // fall through to default
  }

  // 3. Build-time default
  return defaultEnabled;
}

/**
 * Read the URL param + localStorage and return whether chat-v2 is on.
 * See `resolveFlag` for the resolution chain (this was the original
 * implementation; CI-1 extracted it unchanged).
 */
export function isReactChatV2Enabled(): boolean {
  return resolveFlag(URL_PARAM, STORAGE_KEY, DEFAULT_ENABLED);
}

/**
 * CI-1 — whether the /v2/today coach surfaces (CoachCard desktop,
 * CoachStories mobile) render real rules-engine insights. Default ON
 * since CI-3; opt out with `?coach_insights=0`.
 */
export function isCoachInsightsEnabled(): boolean {
  return resolveFlag(INSIGHTS_URL_PARAM, INSIGHTS_STORAGE_KEY, INSIGHTS_DEFAULT_ENABLED);
}

/**
 * NU-2 — whether the plan-lifecycle nudge detectors run on /v2/today
 * (and their `plan_nudge` notifications fire). Default ON since NU-3;
 * opt out with `?plan_nudges=0`.
 */
export function isPlanNudgesEnabled(): boolean {
  return resolveFlag(PLAN_NUDGES_URL_PARAM, PLAN_NUDGES_STORAGE_KEY, PLAN_NUDGES_DEFAULT_ENABLED);
}

/**
 * NP-0 — whether chat messages that persisted a plan render the canonical
 * DB-truth table (suppressing the LLM's own markdown table). Default ON;
 * opt out with `?canonical_plan_table=0`.
 */
export function isCanonicalPlanTableEnabled(): boolean {
  return resolveFlag(
    CANONICAL_TABLE_URL_PARAM,
    CANONICAL_TABLE_STORAGE_KEY,
    CANONICAL_TABLE_DEFAULT_ENABLED,
  );
}

/**
 * B-19 — whether the WeekPlan grid + PlanTable card surface the planned-vs-
 * actual pairing (and the reward strip sums actuals of completed days). Default
 * OFF; opt in with `?b19_actuals=1`.
 *
 * TODO (REQUIRED before any GA / soft-launch ON — founder-ack'd in review): this
 * URL/localStorage `resolveFlag` mechanism is ACCEPTED for PREVIEW only (matches
 * the 4 existing frontend-flag precedents). Before flipping ON for real users,
 * convert B-19 to the DB `feature_flags` / `useServerFlag('b19_actuals_v1')`
 * mechanism — founder-flippable, `/api/config` parity, matching the DB-gated
 * write flag `actual_distance_capture_v1`.
 */
export function isB19ActualsEnabled(): boolean {
  return resolveFlag(B19_ACTUALS_URL_PARAM, B19_ACTUALS_STORAGE_KEY, B19_ACTUALS_DEFAULT_ENABLED);
}

/** For tests / dev: programmatically set the flag without URL or localStorage. */
export function _setReactChatV2EnabledForTests(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    // PR 10: explicit 'false' for opt-out (mirrors the URL-param path
    // post-flip; before PR 10 this called removeItem because the
    // default was OFF).
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // best effort
  }
}

/** Test-only escape hatch: clear the flag entirely so the build-time
 *  default takes over. Equivalent to a fresh user visiting for the
 *  first time post-PR-10. */
export function _clearReactChatV2FlagForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best effort
  }
}

export const _FLAG_STORAGE_KEY = STORAGE_KEY;
export const _FLAG_URL_PARAM = URL_PARAM;
export const _FLAG_DEFAULT_ENABLED = DEFAULT_ENABLED;

export const _INSIGHTS_STORAGE_KEY = INSIGHTS_STORAGE_KEY;
export const _INSIGHTS_URL_PARAM = INSIGHTS_URL_PARAM;
export const _INSIGHTS_DEFAULT_ENABLED = INSIGHTS_DEFAULT_ENABLED;

export const _PLAN_NUDGES_STORAGE_KEY = PLAN_NUDGES_STORAGE_KEY;
export const _PLAN_NUDGES_URL_PARAM = PLAN_NUDGES_URL_PARAM;
export const _PLAN_NUDGES_DEFAULT_ENABLED = PLAN_NUDGES_DEFAULT_ENABLED;

export const _CANONICAL_TABLE_STORAGE_KEY = CANONICAL_TABLE_STORAGE_KEY;
export const _CANONICAL_TABLE_URL_PARAM = CANONICAL_TABLE_URL_PARAM;
export const _CANONICAL_TABLE_DEFAULT_ENABLED = CANONICAL_TABLE_DEFAULT_ENABLED;

export const _B19_ACTUALS_STORAGE_KEY = B19_ACTUALS_STORAGE_KEY;
export const _B19_ACTUALS_URL_PARAM = B19_ACTUALS_URL_PARAM;
export const _B19_ACTUALS_DEFAULT_ENABLED = B19_ACTUALS_DEFAULT_ENABLED;
